import { useState } from "react";
import { format } from "date-fns";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { grantAccess, revokeAccess } from "@/services/adminCrm";
import type { CrmUserSubscription } from "@/services/adminCrm";

interface Props {
  userId: string;
  subscription?: CrmUserSubscription;
  onMutate: () => void;
}

function shortDate(dateStr: string | null): string {
  if (!dateStr) return "\u2014";
  try {
    return format(new Date(dateStr), "MMM d, yyyy");
  } catch {
    return "\u2014";
  }
}

const stateColors: Record<string, string> = {
  active: "bg-green-100 text-green-800 border-green-200",
  comp: "bg-blue-100 text-blue-800 border-blue-200",
  canceling: "bg-amber-100 text-amber-800 border-amber-200",
  expired: "bg-red-100 text-red-800 border-red-200",
  canceled: "bg-red-100 text-red-800 border-red-200",
  past_due: "bg-red-100 text-red-800 border-red-200",
  none: "bg-gray-100 text-gray-600 border-gray-200",
};

export function UserSubscriptionSection({ userId, subscription, onMutate }: Props) {
  const [grantMonths, setGrantMonths] = useState("3");
  const [granting, setGranting] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const state = subscription?.state ?? "none";

  async function handleGrant() {
    const months = parseInt(grantMonths, 10);
    if (isNaN(months) || months < 1 || months > 12) {
      toast.error("Enter a value between 1 and 12");
      return;
    }
    setGranting(true);
    try {
      await grantAccess(userId, months);
      toast.success(`${months} free month${months > 1 ? "s" : ""} added`);
      onMutate();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to grant access");
    } finally {
      setGranting(false);
    }
  }

  async function handleRevoke() {
    setRevoking(true);
    try {
      await revokeAccess(userId);
      toast.success("Comp access revoked");
      onMutate();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to revoke access");
    } finally {
      setRevoking(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Subscription</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">State</span>
            <Badge
              variant="outline"
              className={`text-xs ${stateColors[state] || stateColors.none}`}
            >
              {state.charAt(0).toUpperCase() + state.slice(1).replace("_", " ")}
            </Badge>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Plan</span>
            <span className="capitalize">{subscription?.planType || "\u2014"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Period End</span>
            <span>{shortDate(subscription?.currentPeriodEnd ?? null)}</span>
          </div>
          {subscription?.cancelAtPeriodEnd && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cancel at Period End</span>
              <span className="text-amber-600 font-medium">Yes</span>
            </div>
          )}
          {subscription?.subscriptionOverrideEnd && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Comp Expires</span>
              <span>{shortDate(subscription.subscriptionOverrideEnd)}</span>
            </div>
          )}
          {subscription?.stripeCustomerUrl && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Stripe</span>
              <a
                href={subscription.stripeCustomerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-xs inline-flex items-center gap-1"
              >
                View in Stripe <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
        </div>

        {/* Grant / Revoke */}
        <div className="border-t pt-3 flex items-center gap-2">
          <Input
            type="number"
            min="1"
            max="12"
            value={grantMonths}
            onChange={e => setGrantMonths(e.target.value)}
            className="w-20"
            placeholder="Months"
          />
          <Button size="sm" onClick={handleGrant} disabled={granting}>
            {granting ? "Adding..." : "Add Free Months"}
          </Button>
          {state === "comp" && (
            <Button size="sm" variant="destructive" onClick={handleRevoke} disabled={revoking}>
              {revoking ? "Revoking..." : "Revoke Comp"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
